import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import payrollService from '../../services/payrollService';
import staffAttendanceService from '../../services/staffAttendanceService';
import ClockInOutWidget from '../../components/ClockInOutWidget';

function formatCurrency(amount) {
  return `GHS ${Number(amount || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function TeacherPayslipsSection() {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(true);

  useEffect(() => {
    payrollService.getMyPayslips()
      .then((data) => setPayslips(data.items || []))
      .catch((err) => toast.error(err.message || 'Unable to load payslips'))
      .finally(() => setLoading(false));

    staffAttendanceService.getMyAttendance()
      .then((data) => setAttendance(data))
      .catch(() => setAttendance(null))
      .finally(() => setAttendanceLoading(false));
  }, []);

  const handleDownload = (payslip) => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payslip - ${payslip.period_name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          .container { max-width: 600px; margin: 0 auto; border: 1px solid #ddd; padding: 30px; }
          .header { text-align: center; margin-bottom: 24px; border-bottom: 2px solid #007bff; padding-bottom: 16px; }
          .line { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #eee; font-size: 13px; }
          .line.total { font-weight: bold; border-top: 2px solid #333; border-bottom: 2px solid #333; padding: 8px 0; margin-top: 8px; }
          .line.net { font-weight: bold; font-size: 16px; background: #e3f2fd; padding: 10px 0; margin-top: 8px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header"><h2>PAYSLIP</h2><p>${payslip.period_name}</p></div>
          <div class="line"><span>Basic Salary</span><span>${formatCurrency(payslip.basic_salary)}</span></div>
          <div class="line"><span>Allowances</span><span>${formatCurrency(payslip.total_allowances)}</span></div>
          <div class="line total"><span>Gross Amount</span><span>${formatCurrency(payslip.gross_amount)}</span></div>
          <div class="line"><span>Tax</span><span>-${formatCurrency(payslip.tax_amount)}</span></div>
          <div class="line"><span>Pension</span><span>-${formatCurrency(payslip.pension_amount)}</span></div>
          <div class="line"><span>NSSF</span><span>-${formatCurrency(payslip.nssf_amount)}</span></div>
          <div class="line"><span>Other Deductions</span><span>-${formatCurrency(payslip.other_deductions)}</span></div>
          ${payslip.total_adjustments ? `<div class="line"><span>Adjustments</span><span>${formatCurrency(payslip.total_adjustments)}</span></div>` : ''}
          <div class="line total"><span>Total Deductions</span><span>-${formatCurrency(payslip.total_deductions)}</span></div>
          <div class="line net"><span>NET AMOUNT</span><span>${formatCurrency(payslip.net_amount)}</span></div>
        </div>
      </body>
      </html>
    `;
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => printWindow.print();
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="section-card">
        <div className="section-header">
          <div>
            <h3>My Payslips</h3>
            <p>Your finalized salary history — a payslip appears here once its payroll run has been posted.</p>
          </div>
        </div>

        {loading ? (
          <div className="empty-state"><p>Loading...</p></div>
        ) : payslips.length === 0 ? (
          <div className="empty-state"><p>No payslips yet.</p></div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {payslips.map((p) => (
              <div key={p.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <strong>{p.period_name}</strong>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      Gross {formatCurrency(p.gross_amount)} · Deductions {formatCurrency(p.total_deductions)}
                      {p.total_adjustments ? ` · Adjustments ${formatCurrency(p.total_adjustments)}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{
                      fontSize: 12, padding: '2px 8px', borderRadius: 8,
                      color: p.payment_status === 'paid' ? '#15803d' : p.payment_status === 'failed' ? '#991b1b' : '#6b7280',
                      background: p.payment_status === 'paid' ? '#f0fdf4' : p.payment_status === 'failed' ? '#fef2f2' : '#f3f4f6',
                    }}>
                      {p.payment_status === 'paid' ? 'Paid' : p.payment_status === 'failed' ? 'Payment failed' : 'Unpaid'}
                    </span>
                    <strong>{formatCurrency(p.net_amount)}</strong>
                    <button type="button" className="button-secondary small" onClick={() => handleDownload(p)}>Download</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="section-card">
        <div className="section-header">
          <div>
            <h3>My Attendance</h3>
            <p>Your recorded attendance history — this is what payroll's attendance-based deductions are calculated from.</p>
          </div>
        </div>

        <div style={{ maxWidth: 320, marginBottom: 20 }}>
          <ClockInOutWidget />
        </div>

        {attendanceLoading ? (
          <div className="empty-state"><p>Loading...</p></div>
        ) : !attendance || attendance.records.length === 0 ? (
          <div className="empty-state"><p>No attendance recorded yet.</p></div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
              <div><strong>{attendance.summary.present}</strong><div style={{ fontSize: 12, color: '#475569' }}>Present</div></div>
              <div><strong>{attendance.summary.absent}</strong><div style={{ fontSize: 12, color: '#475569' }}>Absent</div></div>
              <div><strong>{attendance.summary.late}</strong><div style={{ fontSize: 12, color: '#475569' }}>Late</div></div>
              <div><strong>{attendance.summary.excused}</strong><div style={{ fontSize: 12, color: '#475569' }}>Excused</div></div>
              <div><strong>{attendance.summary.attendance_percentage}%</strong><div style={{ fontSize: 12, color: '#475569' }}>Attendance rate</div></div>
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {attendance.records.slice(0, 10).map((r) => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderBottom: '1px solid #f1f5f9', padding: '6px 0' }}>
                  <span>{r.date}</span>
                  <span className="capitalize">{r.status}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TeacherPayslipsSection;
